import React from 'react';
import classNames from 'classnames';
import { Slider, SliderProps } from '@easyv/react-components';
import { isNil, isNumber } from '@/utils';
import { ProInputNumber } from './ProInputNumber';
import { formContentLWidth1, formContentRWidth1 } from '@/constants';

export interface ProInputSliderProp extends Omit<SliderProps, 'onChange'> {
  name: string;
  value: number;
  showInput?: boolean;
  limitSliderOnly?: boolean;
  suffix?: string;
  prefix?: string;
  className?: string;
  precisionOnShow?: number;
  precisionOnSave?: number;
  onChange?: (name: string, value: number) => void;
  onBlur: (name: string, value: number) => void;
  inputToRealValue?: (value: number) => number;
  realToInputValue?: (value: number) => number;
}

export function ProInputSlider({
  name,
  min,
  max,
  limitSliderOnly = true,
  value,
  step = 0.01,
  showInput = true,
  suffix = '',
  prefix = '',
  className,
  precisionOnShow = 2,
  precisionOnSave,
  onChange,
  onBlur,
  disabled,
  inputToRealValue,
  realToInputValue,
  ...props
}: ProInputSliderProp) {
  const [innerValue, setInnerValue] = React.useState<number>(0);

  React.useEffect(() => {
    setInnerValue(realToInputValue ? realToInputValue(value || 0) : value || 0);
  }, [realToInputValue, value]);

  const handleChange = (name: string, e: number | number[] | undefined) => {
    const value: number = isNil(e) ? 0 : typeof e === 'number' ? e : e[0];
    const saveValue = inputToRealValue ? inputToRealValue(value) : value;
    setInnerValue(value);
    onChange?.(name, saveValue);
  };

  const handleBlur = (name: string, e: number | number[] | undefined) => {
    const value: number = isNil(e) ? 0 : typeof e === 'number' ? e : e[0];
    const saveValue = inputToRealValue ? inputToRealValue(value) : value;
    setInnerValue(value);
    onBlur?.(name, saveValue);
  };

  return (
    <div className='flex gap-x-2 items-center'>
      <Slider
        className={classNames(className)}
        style={showInput ? { width: formContentLWidth1 } : { flex: '1 1 0%' }}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        value={innerValue}
        onChange={(num) => {
          handleChange?.(name, num as number);
        }}
        onAfterChange={(num) => {
          handleBlur(name, num as number);
        }}
        {...props}
      />
      {showInput && (
        <div className='flex items-center' style={{ width: formContentRWidth1 }}>
          <ProInputNumber
            name={name}
            disabled={disabled}
            min={limitSliderOnly ? undefined : min}
            max={limitSliderOnly ? undefined : max}
            suffix={suffix}
            prefix={prefix}
            precisionOnShow={precisionOnShow}
            precisionOnSave={precisionOnSave}
            step={step || 0.1}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            inputToRealValue={inputToRealValue}
            realToInputValue={realToInputValue}
          />
        </div>
      )}
    </div>
  );
}
